<template>
    <ToastProvider :duration="4000">
        <ToastRoot
            v-for="toast in items"
            :key="toast.id"
            :open="toast.open"
            class="app-toast__item pointer-events-auto rounded-20 border border-white/15 bg-black/85 p-4 text-ash-200 shadow-[0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-md"
            @update:open="toastStore.setOpen(toast.id, $event)">
            <div class="flex items-start gap-3">
                <div class="min-w-0 flex-1">
                    <ToastTitle class="text-16/132 font-semibold">
                        {{ toast.title }}
                    </ToastTitle>
                </div>

                <ToastClose
                    as="button"
                    aria-label="Закрыть уведомление"
                    class="shrink-0 rounded-full p-1 text-ash-300 transition-colors duration-200 hover:text-white">
                    <AtomIcon
                        name="close"
                        class="block size-4 leading-none"
                        >&times;</AtomIcon
                    >
                </ToastClose>
            </div>

            <div
                v-if="toast.action"
                class="mt-4">
                <ToastAction
                    as-child
                    :alt-text="toast.action.label">
                    <NuxtLink
                        :to="toast.action.to"
                        @click="handleActionClick"
                        class="inline-flex h-11 items-center justify-center rounded-full border border-transparent bg-accent px-5 text-16/120 font-semibold text-ash-200 transition-all duration-300 hover:border-accent hover:bg-transparent hover:text-accent">
                        {{ toast.action.label }}
                    </NuxtLink>
                </ToastAction>
            </div>
        </ToastRoot>

        <ToastViewport
            class="fixed right-4 top-4 z-[70] flex w-[calc(100vw-2rem)] max-w-[26rem] flex-col gap-3 outline-none sm:right-5 sm:top-5 sm:w-full" />
    </ToastProvider>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import {
    ToastAction,
    ToastClose,
    ToastProvider,
    ToastRoot,
    ToastTitle,
    ToastViewport,
} from 'reka-ui'
import { useToastStore } from '~/stores/toast.store'

import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'

const toastStore = useToastStore()
const { items } = storeToRefs(toastStore)

function handleActionClick(): void {
    toastStore.closeAll()
}
</script>

<style scoped>
@keyframes toast-show {
    from {
        opacity: 0;
        transform: translateX(calc(100% + 1.25rem));
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes toast-hide {
    from {
        opacity: 1;
        transform: translateX(0);
    }

    to {
        opacity: 0;
        transform: translateX(calc(100% + 1.25rem));
    }
}

@keyframes toast-swipe-out {
    from {
        opacity: 1;
        transform: translateX(var(--reka-toast-swipe-end-x));
    }

    to {
        opacity: 0;
        transform: translateX(
            calc(var(--reka-toast-swipe-end-x) + 100% + 1.25rem)
        );
    }
}

:deep(.app-toast__item[data-state='open']) {
    animation: toast-show 200ms ease-out;
}

:deep(.app-toast__item[data-state='closed']) {
    animation: toast-hide 200ms ease-in forwards;
}

:deep(.app-toast__item[data-swipe='move']) {
    transform: translateX(var(--reka-toast-swipe-move-x));
}

:deep(.app-toast__item[data-swipe='cancel']) {
    transform: translateX(0);
    transition: transform 200ms ease-out;
}

:deep(.app-toast__item[data-swipe='end']) {
    animation: toast-swipe-out 180ms ease-out forwards;
}
</style>
